import type { QuoteRevisionSnapshot } from "../../types/revision.type";
import type { RfxQuoteItem } from "../../types/quote.type";
import type { QuoteTableRow } from "./quote-table.types";

/**
 * Returns a Set of field keys (e.g. "q-123.quantity", "q-123.rate") that differ
 * between two revisions.
 */
export const getQuoteDiff = (
	a: QuoteRevisionSnapshot,
	b: QuoteRevisionSnapshot
): Set<string> => {
	const diffKeys = new Set<string>();

	const aAnswerMap = new Map(
		a.answers.map((ans) => [ans.rfq_quote_item_id, ans])
	);
	const bAnswerMap = new Map(
		b.answers.map((ans) => [ans.rfq_quote_item_id, ans])
	);

	const allItemIds = new Set([...aAnswerMap.keys(), ...bAnswerMap.keys()]);

	let aSubtotal = 0;
	let bSubtotal = 0;

	allItemIds.forEach((itemId) => {
		const aAns = aAnswerMap.get(itemId);
		const bAns = bAnswerMap.get(itemId);

		if (!aAns || !bAns) {
			diffKeys.add(`q-${itemId}.quantity`);
			diffKeys.add(`q-${itemId}.rate`);
			diffKeys.add(`q-${itemId}.cost`);
			if (aAns) {
				aSubtotal +=
					Number(aAns.answer.value.quantity) * Number(aAns.answer.value.rate);
			}
			if (bAns) {
				bSubtotal +=
					Number(bAns.answer.value.quantity) * Number(bAns.answer.value.rate);
			}
			return;
		}

		const aQty = Number(aAns.answer.value.quantity);
		const bQty = Number(bAns.answer.value.quantity);
		const aRate = Number(aAns.answer.value.rate);
		const bRate = Number(bAns.answer.value.rate);

		aSubtotal += aQty * aRate;
		bSubtotal += bQty * bRate;

		if (aQty !== bQty) {
			diffKeys.add(`q-${itemId}.quantity`);
		}
		if (aRate !== bRate) {
			diffKeys.add(`q-${itemId}.rate`);
		}
		if (aQty * aRate !== bQty * bRate) {
			diffKeys.add(`q-${itemId}.cost`);
		}
	});

	if (aSubtotal !== bSubtotal) {
		diffKeys.add("total.subtotal");
		diffKeys.add("total.gst");
		diffKeys.add("total.totalcost");
	}

	return diffKeys;
};

/**
 * Formats a revision number for display.
 * 0 → 'Original', N → 'Revision N'
 */
export const formatRevisionLabel = (revision: number): string => {
	if (revision === 0) {
		return "Original";
	}
	return `Revision ${revision}`;
};

/**
 * Formats a number as USD currency.
 */
export const formatCurrency = (value: number): string =>
	value.toLocaleString("en-US", { style: "currency", currency: "USD" });

/**
 * Total row types used by QuoteTableView and QuoteTableEdit.
 */
export enum TotalType {
	GST = "gst",
	SubTotal = "subtotal",
	Total = "totalcost",
}

export const SUBTOTAL_ROW = {
	id: TotalType.SubTotal,
	label: "Subtotal",
	color: "#475467",
} as const;

export const TOTAL_ROWS = [
	SUBTOTAL_ROW,
	{ id: TotalType.GST, label: "GST (10%)", color: "#475467" },
	{ id: TotalType.Total, label: "Total Cost", color: "#101828" },
] as const;

/**
 * Calculates subtotal, GST, or total cost from an array of QuoteTableRows.
 */
export const calculateTotalFromRows = (
	rows: QuoteTableRow[],
	type: TotalType
): number => {
	const subtotal = rows.reduce(
		(acc, row) =>
			acc +
			(isNaN(row.quantity) ? 0 : row.quantity) *
				(isNaN(row.rate) ? 0 : row.rate),
		0
	);
	switch (type) {
		case TotalType.SubTotal:
			return subtotal;
		case TotalType.GST:
			return subtotal * 0.1;
		case TotalType.Total:
			return subtotal + subtotal * 0.1;
		default:
			return 0;
	}
};

/**
 * Converts a QuoteRevisionSnapshot's answers into QuoteTableRow[].
 * When `quoteItems` is provided, item names and unit labels are resolved
 * from the quote definition rather than showing generic "Item N".
 */
export const mapSnapshotToRows = (
	snapshot: QuoteRevisionSnapshot,
	quoteItems?: RfxQuoteItem[]
): QuoteTableRow[] => {
	const itemMap = quoteItems
		? new Map(quoteItems.map((item) => [item.id, item]))
		: undefined;

	return snapshot.answers
		.map((ans) => {
			const item = itemMap?.get(ans.rfq_quote_item_id);
			return {
				itemId: ans.rfq_quote_item_id,
				name: item?.name ?? `Item ${ans.rfq_quote_item_id}`,
				unitLabel: item?.unit?.name,
				quantity: Number(ans.answer.value.quantity),
				rate: Number(ans.answer.value.rate),
				sort_order: Number(item?.sort_order),
			};
		})
		.sort((a, b) => a.sort_order - b.sort_order);
};
